import { buildService } from '@rd-web-markets/shared/dist/services/service';
import serviceMethods from '@rd-web-markets/shared/dist/services/service_methods';
import { useCallback, useState } from 'react';
import { replaceInArray } from '@rd-web-markets/shared/dist/util/arrayUtils/arrayUtils';
const api = buildService('/project_elster_details/{id}/elster_asset_instances', {
  only: ['all', 'get', 'update', 'delete', 'create']
});
const assetApi = buildService('/claim_project_reports/{id}/assets', {
  only: ['update']
});
const elsterAssetInstancesService = {
  useFetchAll: function (projectElsterDetailId, setListLoading) {
    let fetchImmediately = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : true;
    const [assetInstances, setAssetInstances, fetchAssetInstances] = serviceMethods.useFetch({
      callback: useCallback(async () => await api.all(projectElsterDetailId), [projectElsterDetailId]),
      setLoading: setListLoading,
      fetchImmediately
    });
    return [assetInstances, setAssetInstances, fetchAssetInstances];
  },
  useGet: setDetailLoading => {
    const [assetInstance, setAssetInstance, fetchAssetInstance] = serviceMethods.useFetch({
      callback: useCallback(async _ref => {
        let {
          projectElsterDetailId,
          assetInstanceId
        } = _ref;
        return await api.get(projectElsterDetailId, assetInstanceId);
      }, []),
      setLoading: setDetailLoading,
      fetchImmediately: false,
      initialStateValue: null
    });
    return [assetInstance, setAssetInstance, fetchAssetInstance];
  },
  useUpdateAssetInstance: (projectElsterDetailId, setDetailLoading, setAssetInstances, setAssetInstance) => {
    const updateAssetInstance = serviceMethods.useUpdate({
      callback: useCallback(async assetInstance => {
        const updatedAssetInstance = await api.update(projectElsterDetailId, assetInstance.id, {
          elster_asset_instance: assetInstance
        });

        // Update assetInstance in the array (this also updates the nested asset)
        if (setAssetInstances) {
          setAssetInstances(replaceInArray(ai => ai.id === updatedAssetInstance.id, updatedAssetInstance));
        }

        // Update the assetInstance state
        if (setAssetInstance) {
          setAssetInstance(updatedAssetInstance);
        }
        return updatedAssetInstance;
      }, [projectElsterDetailId, setAssetInstances, setAssetInstance]),
      setLoading: setDetailLoading
    });
    return [updateAssetInstance];
  },
  useUpdateAsset: (reportId, setDetailLoading) => {
    const updateAsset = serviceMethods.useUpdate({
      callback: useCallback(async asset => {
        const updatedAsset = await assetApi.update(reportId, asset.id, {
          asset
        });
        return updatedAsset;
      }, [reportId]),
      setLoading: setDetailLoading
    });
    return [updateAsset];
  },
  useCreate: (projectElsterDetailId, setDetailLoading, setAssetInstances, setAssetInstance, setSelectedAssetInstance) => {
    const createAssetInstance = serviceMethods.useCreate({
      callback: useCallback(async params => {
        const newAssetInstance = await api.create(projectElsterDetailId, params);
        if (setAssetInstances) {
          setAssetInstances(assets => {
            return [...assets, newAssetInstance];
          });
        }

        // Update the assetInstance state
        if (setAssetInstance) {
          setAssetInstance(newAssetInstance);
        }
        if (setSelectedAssetInstance) {
          setSelectedAssetInstance(newAssetInstance.id);
        }
        return newAssetInstance;
      }, [projectElsterDetailId, setAssetInstances, setAssetInstance, setSelectedAssetInstance]),
      setLoading: setDetailLoading
    });
    return [createAssetInstance];
  },
  useDelete: (projectElsterDetailId, setListLoading, setAssetInstances, setSelectedAssetInstance) => {
    const deleteAssetInstance = serviceMethods.useDelete({
      callback: useCallback(async assetInstance => {
        await api.delete(projectElsterDetailId, assetInstance.id);
        if (setAssetInstances) {
          setAssetInstances(assets => {
            const assetsFiltered = assets.filter(a => a.id !== assetInstance.id);
            return [...assetsFiltered];
          });
        }
        if (setSelectedAssetInstance) {
          setSelectedAssetInstance(null);
        }
      }, [projectElsterDetailId, setAssetInstances, setSelectedAssetInstance]),
      setLoading: setListLoading
    });
    return [deleteAssetInstance];
  }
};
elsterAssetInstancesService.useCrud = function (projectElsterDetailId, reportId, setListLoading, setDetailLoading, setSelectedAssetInstance) {
  let fetchImmediately = arguments.length > 5 && arguments[5] !== undefined ? arguments[5] : true;
  const [assetInstances, setAssetInstances, fetchAssetInstances] = elsterAssetInstancesService.useFetchAll(projectElsterDetailId, setListLoading, fetchImmediately);
  const [assetInstance, setAssetInstance, fetchAssetInstance] = elsterAssetInstancesService.useGet(setDetailLoading);
  const [updateAssetInstance] = elsterAssetInstancesService.useUpdateAssetInstance(projectElsterDetailId, setDetailLoading, setAssetInstances, setAssetInstance);
  const [updateAsset] = elsterAssetInstancesService.useUpdateAsset(reportId, setDetailLoading);
  const [createAssetInstance] = elsterAssetInstancesService.useCreate(projectElsterDetailId, setDetailLoading, setAssetInstances, setAssetInstance, setSelectedAssetInstance);
  const [deleteAssetInstance] = elsterAssetInstancesService.useDelete(projectElsterDetailId, setListLoading, setAssetInstances, setSelectedAssetInstance);
  return {
    assetInstances,
    setAssetInstances,
    fetchAssetInstances,
    assetInstance,
    setAssetInstance,
    fetchAssetInstance,
    updateAssetInstance,
    updateAsset,
    createAssetInstance,
    deleteAssetInstance
  };
};
export default elsterAssetInstancesService;